import Head from 'next/head';
import Link from 'next/link';

import clsxm from '@/lib/clsxm';

import {
  buttonSecondary,
  SectionHeading,
  tone,
} from '@/components/agents/primitives';

import { useEmployeesT, useHireHref } from './kit';

type Faq = { question: string; answer: string };

export type PlanLimits = {
  free: number;
  starter: number;
  unlimited: number;
  teams: number;
};

export default function EmployeesFaq({
  limits,
  appTotal,
}: {
  limits: PlanLimits;
  appTotal: number;
}) {
  const { t } = useEmployeesT();
  const hireHref = useHireHref();
  // The answer names 12 brands (Outlook is mail and calendar: 13 apps).
  const others = Math.max(0, appTotal - 13);

  const faqs: Faq[] = [
    {
      question: t('What is an AI employee?'),
      answer: t(
        'An AI teammate with a role, its own instructions, a memory and the apps you connect to it. You talk to it like a colleague, in a direct chat or in a group chat with the rest of your team, and it can also work on a schedule.',
      ),
    },
    {
      question: t('How is this different from Marky Agent?'),
      answer: t(
        'Marky Agent is your all-round assistant for one task at a time. AI employees are a team: each one has its own role, memory and apps, they hand work to each other in group chats, keep a task list and run routines on their own schedule.',
      ),
    },
    {
      question: t('How is this different from AI Agents?'),
      answer: t(
        'AI Agents answer your customers on your website, WhatsApp, email and the phone. AI employees work for you and your team inside Easy-Peasy.AI.',
      ),
    },
    {
      question: t('Can they send emails or change things on their own?'),
      answer: t(
        'Not unless you let them. By default, every email, calendar change and change in your other apps waits for your approval, and you can approve it, edit it or reject it. You can switch approvals off for each employee and each kind of action.',
      ),
    },
    {
      question: t('How many AI employees can I hire?'),
      answer: t(
        '{{free}} on the Free plan, {{starter}} on Starter, {{unlimited}} on the Unlimited plans and {{teams}} for each person on a Teams plan.',
        limits,
      ),
    },
    {
      question: t('Which apps can they use?'),
      answer: t(
        'Gmail, Google Calendar and Drive, Outlook, OneDrive, OneNote, Notion, GitHub, Canva, Stripe, PostHog, Supabase and {{count}} more apps, plus any MCP server or API you add. Each employee only uses the apps you connect to it.',
        { count: others },
      ),
    },
    {
      question: t('Do they keep working when I close my laptop?'),
      answer: t(
        'Yes. Routines run on our servers on their schedule, and work an employee has started carries on if you close the tab. The results are in your chat when you’re back.',
      ),
    },
    {
      question: t('Which AI models do they use?'),
      answer: t(
        'You choose for each employee, from fast models to the most capable ones from OpenAI, Anthropic, Google and others, and how much effort it puts in. Gmail, Google Calendar and Google Drive can’t be used with Grok or DeepSeek models.',
      ),
    },
    {
      question: t('Can I change a role after hiring?'),
      answer: t(
        'Yes. Rename an employee or change its portrait, instructions, model, apps and knowledge at any time. You can also ask it in a chat to update its own profile.',
      ),
    },
    {
      question: t('Is my data safe?'),
      answer: t(
        'We don’t use your inputs to train our own models, and each employee only sees the apps you connect to it. Easy-Peasy.AI is SOC 2, ISO 27001, GDPR and HIPAA compliant.',
      ),
    },
  ];

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: { '@type': 'Answer', text: answer },
    })),
  };

  return (
    <section id='faq' className='py-20 sm:py-28'>
      <Head>
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(structuredData).replace(/</g, '\\u003c'),
          }}
        />
      </Head>
      <div className='mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-8'>
        <div className='lg:col-span-4'>
          <SectionHeading
            size='md'
            title={t('Questions, answered.')}
            lead={t(
              'Still wondering how an AI team fits your work? Hire your first employee for free and ask it.',
            )}
          />
          <Link href={hireHref} className={clsxm(buttonSecondary, 'mt-8')}>
            {t('Hire your first employee')}
          </Link>
        </div>

        <div className='lg:col-span-8'>
          <ul className='divide-y divide-[#0f0f28]/[0.08] border-y border-[#0f0f28]/[0.08] dark:divide-white/[0.08] dark:border-white/[0.08]'>
            {faqs.map(({ question, answer }) => (
              <li key={question}>
                <details className='group'>
                  <summary
                    className={clsxm(
                      'flex cursor-pointer list-none items-center justify-between gap-6 py-6 text-start text-lg font-semibold transition-colors hover:text-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary-600 dark:hover:text-primary-300 [&::-webkit-details-marker]:hidden',
                      tone.ink,
                    )}
                  >
                    {question}
                    <span
                      aria-hidden='true'
                      className={clsxm(
                        'relative flex h-8 w-8 flex-none items-center justify-center rounded-full',
                        tone.line,
                      )}
                    >
                      <span className='absolute h-px w-3 bg-current' />
                      <span className='absolute h-3 w-px bg-current transition-transform duration-300 group-open:rotate-90 group-open:opacity-0' />
                    </span>
                  </summary>
                  <p
                    className={clsxm(
                      'max-w-2xl pb-7 pe-12 text-[16px] leading-7',
                      tone.body,
                    )}
                  >
                    {answer}
                  </p>
                </details>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
